import { useState, useEffect, useRef, useCallback } from "react";
import { useScrollLock } from "@/ui";

// Animation Constants
export const VIEWPORT_WIDTH_RATIO_DESKTOP = 0.8; // Modal takes 80% of viewport width on desktop
export const VIEWPORT_WIDTH_RATIO_MOBILE = 0.95; // Modal takes 95% of viewport width on mobile
const VIEWPORT_HEIGHT_RATIO_DESKTOP = 0.8; // Modal takes 80% of viewport height on desktop
const VIEWPORT_HEIGHT_RATIO_MOBILE = 0.95; // Modal takes 95% of viewport height on mobile

type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

interface UseModalScaleTransitionOptions {
  duration: number;
  maxPageWidth: number;
  isMobile: boolean;
}

interface UseModalScaleTransitionReturn {
  isOpen: boolean;
  isMeasuring: boolean;
  isInComplete: boolean;
  startRect: Rect | null;
  contentHeight: number | null;
  triggerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  handleOpen: () => void;
  handleClose: (e?: React.MouseEvent) => void;
  transitionConfig: ReturnType<typeof getScaleModalTransition>;
}

function getScaleModalTransition(
  startRect: Rect | null,
  contentHeight: number | null,
  maxPageWidth: number,
  isMobile: boolean,
) {
  if (!startRect || !contentHeight) {
    // Fallback for initial render
    return {
      in: { opacity: 1 },
      out: { opacity: 0 },
      common: {},
      transitionProperty: "opacity",
    };
  }

  // Calculate actual pixel values for smooth animation
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  // Use mobile-specific ratios for wider modal on small screens
  const widthRatio = isMobile ? VIEWPORT_WIDTH_RATIO_MOBILE : VIEWPORT_WIDTH_RATIO_DESKTOP;
  const heightRatio = isMobile ? VIEWPORT_HEIGHT_RATIO_MOBILE : VIEWPORT_HEIGHT_RATIO_DESKTOP;

  // Use max page width from theme (only applies on desktop)
  const targetWidth = Math.min(viewportWidth * widthRatio, maxPageWidth);
  const maxHeight = viewportHeight * heightRatio;

  // Use measured content height, capped at maxHeight
  const targetHeight = Math.min(contentHeight, maxHeight);

  // Calculate scale factors
  const scaleX = startRect.width / targetWidth;
  const scaleY = startRect.height / targetHeight;

  // Target position (centered)
  const targetTop = viewportHeight * 0.5 - targetHeight / 2;
  const targetLeft = viewportWidth * 0.5 - targetWidth / 2;

  return {
    in: {
      top: targetTop,
      left: targetLeft,
      width: targetWidth,
      height: targetHeight,
      transform: "scale(1)",
    },
    out: {
      opacity: 0,
      top: targetTop,
      left: targetLeft,
      width: targetWidth,
      height: targetHeight,
      transform: `translate(${startRect.left - targetLeft}px, ${
        startRect.top - targetTop
      }px) scale(${scaleX}, ${scaleY})`,
    },
    common: { transformOrigin: "top left" },
    transitionProperty: "transform, opacity",
  };
}

/**
 * Custom hook for managing App Store-style modal scale transitions.
 *
 * This hook handles:
 * - Measuring content dimensions before animation
 * - Managing modal open/close state
 * - Calculating transform values for smooth scaling
 * - Scroll lock during modal display
 * - Mobile-specific sizing for better UX on small screens
 *
 * @param options - Configuration options
 * @returns Modal state and handlers
 */
export function useModalScaleTransition({
  duration,
  maxPageWidth,
  isMobile,
}: UseModalScaleTransitionOptions): UseModalScaleTransitionReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [isInComplete, setIsInComplete] = useState(false);
  const [, setScrollLocked] = useScrollLock();
  const [startRect, setStartRect] = useState<Rect | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpen = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setStartRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      // First, render content hidden to measure it
      setIsMeasuring(true);
      clearTimeout(timeoutRef.current ?? 0);
      timeoutRef.current = setTimeout(() => {
        setIsInComplete(true);
      }, duration + 50);
    }
  }, [duration]);

  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      // Clear the timeout if animation hasn't completed yet
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsInComplete(false);
      setIsOpen(false);
      setTimeout(() => {
        setScrollLocked(false);
        setContentHeight(null);
        setIsMeasuring(false);
      }, duration);
    },
    [duration, setScrollLocked],
  );

  // Measure content height, then start transition
  useEffect(() => {
    if (isMeasuring && contentRef.current && !isOpen) {
      // Measure the content
      requestAnimationFrame(() => {
        if (contentRef.current) {
          const height = contentRef.current.getBoundingClientRect().height;
          setContentHeight(Math.ceil(height));
          // Now start the actual transition
          setIsOpen(true);
          setScrollLocked(true);
          setIsMeasuring(false);
        }
      });
    }
  }, [isMeasuring, isOpen, setScrollLocked]);

  const transitionConfig = getScaleModalTransition(startRect, contentHeight, maxPageWidth, isMobile);

  return {
    isOpen,
    isMeasuring,
    isInComplete,
    startRect,
    contentHeight,
    triggerRef,
    contentRef,
    handleOpen,
    handleClose,
    transitionConfig,
  };
}
